<template>
    <div class="container-fluid form-container fire-alarm-test-form-container">
        <div class="row">
            <div class="col-sm-12">
                <h1 class="page-title">
                    Fire Alarm Commissioning Certificate
                </h1>
            </div>
        </div>
        <form
            action=""
            class="fire-alarm-certificate"
            @submit.prevent="saveForm()"
        >
            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-sm-12 col-md-6 form-group">
                            <label for="customer">Customer</label>
                            <customer-search
                                ref="customerSearch"
                                :customer="form.customer"
                                @change="customerChanged"
                                :customer-id="form.customer_id"
                            />
                            <error
                                name="customer_id"
                                :errors="errors"
                            />
                        </div>
                        <div
                            class="col-sm-12 col-md-6 form-group"
                            v-if="form.customer && form.customer.sites"
                        >
                            <label for="site">Site:</label>
                            <select
                                name="site"
                                v-model="form.site_id"
                                id="site"
                                class="form-control"
                                placeholder="Site"
                                required
                                v-if="form.customer.sites.length"
                                @change="siteChanged()"
                            >
                                <option
                                    v-for="site in form.customer.sites"
                                    :key="site.id"
                                    :value="site.id"
                                >
                                    #{{ site.site_no }} {{ site.name }}
                                </option>
                            </select>
                            <span
                                class="form-error"
                                v-if="!form.customer.sites.length"
                            >This customer has no sites</span>
                            <error
                                name="site_id"
                                :errors="errors"
                            />
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-sm-12 col-md-3 form-group">
                            <label for="job_id">Job No.</label>
                            <select
                                v-if="jobs"
                                name="job_id"
                                id="job_id"
                                v-model="form.job_id"
                                class="form-control"
                                placeholder="Select a job"
                                :disabled="jobs.length < 1"
                            >
                                <option
                                    :value="null"
                                    disabled
                                >
                                    Select a job
                                </option>
                                <option
                                    v-for="job in jobs"
                                    :key="job.id"
                                    :value="job.id"
                                >
                                    {{ job.id }}: {{ job.name }}
                                </option>
                            </select>
                            <error
                                name="job_id"
                                :errors="errors"
                            />
                        </div>

                        <div class="col-sm-12 col-md-3 form-group">
                            <label for="doc_no">Document No.</label>
                            <input
                                type="text"
                                id="doc_no"
                                name="doc_no"
                                v-model="form.doc_no"
                                placeholder="Document Number"
                                class="form-control"
                            >
                            <error
                                name="doc_no"
                                :errors="errors"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-sm-12">
                            <p>
                                I/we being the competent person(s) responsible (as indicated by my/our signatures below)
                                for the commissioning of the fire alarm system, particulars of which are set out below,
                                CERTIFY that the said work for which I/we have been responsible complies to the best of
                                my/our knowledge and belief with the specification described below and with the
                                recommendations of Clause 39 of BS 5839-1:2017, except for the variations, if any stated
                                in the certificate.
                            </p>
                        </div>
                    </div>

                    <div class="row form-group">
                        <div class="col-12 col-md-6 d-flex align-items-center">
                            <label
                                for="engineer_id"
                                class="mb-0"
                            >Engineer name:</label>
                            <user-select-component
                                v-model="form.engineer_id"
                                :admins="false"
                                :office="false"
                                class="w-auto ml-1"
                            />
                        </div>

                        <div class="col-12 col-md-6 d-flex align-items-center justify-content-end">
                            <label
                                for="engineer_position"
                                class="mb-0 mr-2"
                            >Engineer position:</label>
                            <input
                                v-model="form.engineer_position"
                                class="form-control w-auto"
                            >
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-12 col-md-6 d-flex align-items-center ml-auto justify-content-end">
                            <label
                                class="mb-0"
                            >Date:</label>
                            <datetimepicker
                                v-model="form.form_date"
                                :options="{format: 'L'}"
                                class="mb-0 ml-1"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h3>Final Checks</h3>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-sm-12 d-flex align-items-center">
                            <input
                                type="checkbox"
                                class="mr-2"
                                id="equipment_operates_correctly"
                                v-model="form.equipment_operates_correctly"
                            >
                            <label
                                class="mb-0"
                                for="equipment_operates_correctly"
                            >
                                All equipment operates correctly
                            </label>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-sm-12 d-flex align-items-center">
                            <input
                                type="checkbox"
                                class="mr-2"
                                id="installation_acceptable_standard"
                                v-model="form.installation_acceptable_standard"
                            >
                            <label
                                class="mb-0"
                                for="installation_acceptable_standard"
                            >
                                Installation work is, as far as can reasonably be ascertained, of an acceptable standard
                            </label>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-sm-12 d-flex align-items-center">
                            <input
                                type="checkbox"
                                class="mr-2"
                                id="system_inspected"
                                v-model="form.system_inspected"
                            >
                            <label
                                class="mb-0"
                                for="system_inspected"
                            >
                                The entire system has been inspected and tested in accordance with recommendations of
                                39.2c of BS 5839-1:2017
                            </label>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-sm-12 d-flex align-items-center">
                            <input
                                type="checkbox"
                                class="mr-2"
                                id="system_performs"
                                v-model="form.system_performs"
                            >
                            <label
                                class="mb-0"
                                for="system_performs"
                            >
                                The system performs as required by the specification prepared by:
                            </label>
                            <input
                                type="text"
                                v-model="form.system_performs_by"
                                class="form-control flex-grow-0 w-auto mx-2"
                            >
                            <label>a copy of which I/we have been given</label>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-sm-12 d-flex align-items-center">
                            <input
                                type="checkbox"
                                class="mr-2"
                                id="no_unacceptable_alarm_rate"
                                v-model="form.no_unacceptable_alarm_rate"
                            >
                            <label
                                class="mb-0"
                                for="no_unacceptable_alarm_rate"
                            >
                                Taking into account the guidance contained in Section 3 of BS 5839-1:2017, I/we have not
                                identifies any obvious potential for an unacceptable rate of false alarms.
                            </label>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-sm-12 d-flex align-items-center">
                            <input
                                type="checkbox"
                                class="mr-2"
                                id="documentation_provided"
                                v-model="form.documentation_provided"
                            >
                            <label
                                class="mb-0"
                                for="documentation_provided"
                            >
                                The documentation described in Clause 40 of BS 5839-1:2013 has been provided to the
                                user.
                            </label>
                        </div>
                    </div>

                    <div class="row mb-3 mt-3">
                        <div class="col-sm-12 d-flex flex-column">
                            <label for="liability_extent">
                                The extent of liability of the signatory is limited to the system described below –
                                Extent of system covered by this certificate:
                            </label>
                            <textarea
                                v-model="form.liability_extent"
                                id="liability_extent"
                                class="form-control"
                                rows="2"
                            />
                        </div>
                    </div>

                    <div class="row mb-3 mt-3">
                        <div class="col-sm-12 d-flex flex-column">
                            <label for="variations_text">
                                Variations from the recommendations of clause 39 of (see BS 5839-1:2017, Clause 7)
                            </label>
                            <textarea
                                v-model="form.variations_text"
                                id="variations_text"
                                class="form-control"
                                rows="2"
                            />
                        </div>
                    </div>

                    <div class="row mb-3 mt-3">
                        <div class="col-sm-12 d-flex flex-column">
                            <label for="work_to_completed">
                                The following work should be completed before/after (delete as applicable) the system
                                becomes operational
                            </label>
                            <textarea
                                v-model="form.work_to_completed"
                                id="work_to_completed"
                                class="form-control"
                                rows="2"
                            />
                        </div>
                    </div>

                    <div class="row mb-3 mt-3">
                        <div class="col-sm-12 d-flex flex-column">
                            <label for="false_alarm_causes">
                                The following potential cause of false alarms should be considered at the time of the
                                next service visit
                            </label>
                            <textarea
                                v-model="form.false_alarm_causes"
                                id="false_alarm_causes"
                                class="form-control"
                                rows="2"
                            />
                        </div>
                    </div>

                    <div class="row mb-3 mt-3">
                        <div class="col-sm-12">
                            <div class="d-flex align-items-center flex-wrap">
                                <span class="w-auto">
                                    Before the system becomes operational, it should be soak tested in accordance with the recommendations of 35.2.6 of BS 5839-1:2017 for a period of:
                                </span>
                                <input
                                    v-model="form.soak_tested_duration"
                                    id="soak_tested_duration"
                                    class="form-control w-auto flex-grow-0 mx-2"
                                >
                            </div>
                            <span>
                                (Enter a period of either one week, such period as required by the specification, or such period as recommended by the signatory to this certificate, whichever is the greatest, or delete if not applicable.)
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row form-group">
                        <div class="col-12">
                            <button
                                class="btn btn-success"
                                type="submit"
                                :disabled="isLoading || isSaving"
                            >
                                Save
                            </button>

                            <button
                                class="btn btn-outline-danger"
                                type="button"
                                @click="deleteForm()"
                                :disabled="isLoading || isSaving"
                                v-if="form.id && canDelete"
                            >
                                <i class="fa fa-times" /> Delete
                            </button>

                            <a
                                class="btn btn-info"
                                v-if="form.id"
                                :href="'/api/forms/' + form.id + '/stream'"
                                target="_blank"
                            >
                                <i class="fa fa-print" /> Preview/Print
                            </a>

                            <a
                                class="btn btn-info"
                                v-if="form.id"
                                :href="'/api/forms/' + form.id + '/download'"
                                target="_blank"
                                :download="form.doc_no + '.pdf'"
                            >
                                <i class="fa fa-file" /> Download PDF
                            </a>

                            <router-link
                                class="btn btn-info"
                                v-if="form.id && form.customer_id"
                                :to="{name: 'office.emails.new', params: {customer_id: form.customer_id}, query: {form_id: form.id}}"
                            >
                                <i class="fa fa-envelope" /> Send to Customer
                            </router-link>

                            <customer-site-footer-links
                                :customer-id="form.customer_id"
                                :site-id="form.site_id"
                            />

                            <activity-log
                                v-if="form.id"
                                :id="form.id"
                                model="Form"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import CustomerSearchComponent from "../CustomerSearch.vue";
import FormService from "../../services/FormService";
import * as moment from "moment";
import AuthService from "../../services/AuthService";
import UsesErrors from "../../mixins/UsesErrors";
import SettingsMixin from "../../mixins/SystemSettings";
import UsesInfoMessage from "../../mixins/UsesInfoMessage";
import UsesFormRouteQuery, { formRouteQueryWatchers } from "../../mixins/UsesFormRouteQuery";
import SelectRowComponent from "./parts/SelectRowComponent.vue";
import TextRowComponent from "./parts/TextRowComponent.vue";
import JobService from "../../services/JobService";
import {fire_alarm_systems} from "../../include/Form";
import UserSelectComponent from "../UserSelectComponent.vue";
import Datetimepicker from "../DateTimePickerComponent.vue";

const initialForm = {
    type: 'fire_alarm_commissioning_certificate',
    customer: null,
    form_date: moment(),
    engineer_position: 'Engineer',
};

export default {
    components: {
        Datetimepicker,
        UserSelectComponent,
        "customer-search": CustomerSearchComponent,
    },
    mixins: [
        UsesErrors,
        UsesInfoMessage,
        SettingsMixin,
        UsesFormRouteQuery,
    ],
    data: function () {
        return {
            form: {...initialForm},
            token: null,
            sites: [],
            jobs: [],
        }
    },
    computed: {
        canDelete: function () {
            return AuthService.user.admin_access || AuthService.user.office_access;
        },
        idParam: function () {
            return this.$route.params.id;
        },
        commissioningFormId: function () {
            return this.$route.query.commissioning_form;
        },
    },

    watch: {
        ...formRouteQueryWatchers,
        idParam: {
            handler: function () {
                if (this.idParam) {
                    this.loadForm(this.idParam);
                    return;
                }

                this.form = {...initialForm};
                FormService.nextNo().then(res => {
                    this.form.doc_no = res.data;
                });
            },
            immediate: true,
        },
        commissioningFormId: {
            handler: function () {
                this.loadCommissioningForm();
            },
            immediate: true
        },
    },

    mounted: function () {

        if (!this.form.explained_by_name && AuthService.user.engineer_access) {
            this.form.explained_by_name = AuthService.user.name;
        }
    },

    methods: {
        loadForm: function (id) {
            this.setLoading();
            FormService.get(id).then(res => {
                this.form = res.data;

                if (!this.form.customer) {
                    this.form.customer = {
                        company_name: '',
                    };
                }

                if (res.data.engineer_sign_date) {
                    this.form.explained_on_date = moment(res.data.explained_on_date);
                } else {
                    this.form.explained_on_date = moment();
                }

                if (!this.form.variations || this.form.variations[0] === undefined) {
                    this.form.variations = initialForm.variations;
                }

            }, () => {
                this.setErrorMessage('Could not load form');
            }).then(() => {
                this.clearLoading();
            });
        },

        loadCommissioningForm: function () {
            if (!this.commissioningFormId) {
                return;
            }

            FormService.get(this.commissioningFormId).then(({data}) => {
                this.form.customer_id = data.customer_id;
                this.form.customer = data.customer;
                this.form.site_id = data.site_id;
                this.form.job_id = data.job_id;
                this.form.form_date = moment(data.form_date);
                this.form.engineer_id = data.engineer_id;
                this.form.equipment_operates_correctly = data.equipment_operates_correctly;
                this.form.installation_acceptable_standard = data.installation_acceptable_standard;
                this.form.system_inspected = data.system_inspected;
                this.form.system_performs = data.system_performs;
                this.form.system_performs_by = data.system_performs_by;
                this.form.no_unacceptable_alarm_rate = data.no_unacceptable_alarm_rate;
                this.form.documentation_provided = data.documentation_provided;
                this.form.liability_extent = data.liability_extent;
                this.form.variations_text = data.variations_text;
                this.form.work_to_completed = data.work_to_completed;
                this.form.false_alarm_causes = data.false_alarm_causes;
                this.form.soak_tested_duration = data.soak_tested_duration;
            });
        },

        customerChanged: function (customer) {
            this.form.customer = customer ? customer : {};
            if (this.form.customer) {
                this.form.customer_id = this.form.customer.id;
                this.getJobs();
            } else {
                this.form.customer_id = null;
            }
            if (customer && customer.sites) {
                if (!customer.sites.map(site => site.id).includes(this.form.site_id)) {
                    this.form.site_id = '';
                }
            } else {
                this.form.site_id = '';
            }
        },

        saveForm: function () {
            this.setSaving();
            let data = {...this.form};
            delete data['customer'];
            delete data['site'];
            delete data['job'];
            delete data['data'];

            if (this.form.id) {
                FormService.update(data).then(
                    res => {
                        this.clearSaving();
                        this.setInfoMessage("Form saved successfully");
                        this.$emit('formSaved', res.data);
                    },
                    err => {
                        this.clearSaving();
                        this.captureRequestErrors(err);
                    }
                );
            } else {
                FormService.create(data).then(
                    res => {
                        this.setInfoMessage("Form saved successfully");
                        this.clearSaving();
                        if (this.$route.path.includes('engineer/')) {
                            this.$router.push({
                                name: "engineer.forms.edit",
                                params: {
                                    id: res.data.id
                                }
                            });
                        } else {
                            this.$router.push({
                                name: "office.forms.edit",
                                params: {
                                    id: res.data.id
                                }
                            });
                        }
                    },
                    err => {
                        this.clearSaving();
                        this.captureRequestErrors(err);
                    }
                );
            }
        },

        deleteForm: function () {
            if (confirm("Are you sure you want to delete this form?")) {
                FormService.delete(this.form).then(
                    () => {
                        this.setInfoMessage("Form successfully deleted");
                        this.$router.push({name: 'office.forms.search'});
                    },
                    () => {
                        this.setErrorMessage("Could not delete this form");
                    }
                )
            }
        },

        onJobLoadedFromRoute() {
            this.siteChanged();
            this.getJobs();
        },

        getJobs: function () {
            if (!this.form.customer_id) {
                this.jobs = [];
                this.form.job_id = null;
                return;
            }
            JobService.search({
                "filter[customer_id]": this.form.customer_id,
            }).then(
                (res) => {
                    this.jobs = res.data.data || [];
                },
                (err) => {
                    this.setErrorMessage("Could not load customer jobs");
                    this.jobs = [];
                }
            );
        },
    }
}
</script>


// WEBPACK FOOTER //
// resources/assets/js/components/forms/FireAlarmCommissioningCertificateComponent.vue